import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useUI } from "../../../Context/UIContext";
import {
  getArticleById,
  listArticleCategories,
  listArticles,
  saveArticle,
} from "../../../../services/contentServices";
import { EDITOR_ACCENT } from "../../newsletter-editor/editorUiConstants";
import {
  contentSectionBodySx,
  contentSectionMenubarSx,
  contentSectionMenubarToolbarSx,
} from "../contentEditorStyles";
import ArticleContentEditor from "./ArticleContentEditor";
import ArticlePublicPreview from "./ArticlePublicPreview";
import ArticleMetadataForm from "./ArticleMetadataForm";
import LinkedArticlesSelect from "./LinkedArticlesSelect";
import SuggestedProductsEditor from "./SuggestedProductsEditor";
import { paragraphPlainText } from "../../../../utils/articleParagraphUtils";
import {
  clearArticleEditorSession,
  loadArticleEditorSession,
  saveArticleEditorSession,
  sessionMatchesArticleContext,
} from "./articleEditorSession";
import {
  fromDatetimeLocalToLocalDateTime,
  toDatetimeLocal,
} from "../../../../utils/newsletterDateUtils";

export const createEmptyArticle = () => ({
  id: undefined,
  title: "",
  subtitle: "",
  image: "",
  category: "",
  readingMinutes: 3,
  publishedAt: toDatetimeLocal(new Date().toISOString()),
  isVisible: false,
  content: {
    version: 1,
    blocks: [],
  },
  linkedArticles: [],
  suggestedProducts: [],
});

const normalizeLoadedArticle = (article) => ({
  ...createEmptyArticle(),
  ...article,
  id: article.id ? String(article.id) : undefined,
  subtitle: article.subtitle || "",
  image: article.image || "",
  category: article.category || "",
  readingMinutes: article.readingMinutes || 3,
  publishedAt: toDatetimeLocal(article.publishedAt),
  isVisible: Boolean(article.isVisible),
  linkedArticles: (article.linkedArticles || []).map(String),
  suggestedProducts: article.suggestedProducts || [],
  content: article.content || { version: 1, blocks: [] },
});

const ArticleEditorForm = ({ articleId, onSaved, onReset, onOpenArticle }) => {
  const { showError, showSuccess } = useUI();
  const [article, setArticle] = useState(createEmptyArticle);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(Boolean(articleId));
  const [saving, setSaving] = useState(false);
  const [openList, setOpenList] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewRef = useRef(null);
  const [savedArticles, setSavedArticles] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const resolvedArticleId = articleId || article.id || undefined;

  const pageTitle = useMemo(() => {
    if (!resolvedArticleId) return "Nuovo articolo";
    const label = article.title?.trim();
    return label ? `Modifica: ${label}` : "Modifica articolo";
  }, [article.title, resolvedArticleId]);

  const loadEditor = useCallback(async () => {
    setLoading(true);
    try {
      const categoryRows = await listArticleCategories();
      setCategories(categoryRows);

      const session = loadArticleEditorSession();
      if (sessionMatchesArticleContext(session, articleId) && session.article) {
        setArticle(normalizeLoadedArticle(session.article));
        setPreviewOpen(Boolean(session.previewOpen));
        return;
      }

      if (articleId) {
        setArticle(normalizeLoadedArticle(await getArticleById(articleId)));
      } else {
        setArticle(createEmptyArticle());
      }
      setPreviewOpen(false);
    } catch (error) {
      showError(error.message);
    } finally {
      setLoading(false);
    }
  }, [articleId, showError]);

  useEffect(() => {
    loadEditor();
  }, [loadEditor]);

  useEffect(() => {
    if (loading) {
      return;
    }
    saveArticleEditorSession({
      articleId: resolvedArticleId ?? null,
      article,
      previewOpen,
      workspaceActive: true,
    });
  }, [article, loading, previewOpen, resolvedArticleId]);

  useEffect(() => {
    if (articleId) {
      setPreviewOpen(false);
    }
  }, [articleId]);

  const validateArticle = () => {
    if (!article.title.trim()) return "Inserisci il titolo.";
    if (!article.image.trim()) return "Carica o inserisci l'immagine principale.";
    if (!article.category.trim()) return "Seleziona una categoria.";
    if (!article.content.blocks.length) return "Aggiungi almeno un blocco contenuto.";
    const hasText = article.content.blocks.some(
      (block) => block.type === "paragraph" && paragraphPlainText(block).trim().length > 0
    );
    const hasImage = article.content.blocks.some((block) => block.type === "image");
    if (!hasText && !hasImage) return "Aggiungi testo o almeno un'immagine nel contenuto.";
    return "";
  };

  const handleSave = async () => {
    const validationError = validateArticle();
    if (validationError) {
      showError(validationError);
      return;
    }
    setSaving(true);
    try {
      const saved = await saveArticle({
        ...article,
        id: resolvedArticleId,
        publishedAt: fromDatetimeLocalToLocalDateTime(article.publishedAt),
      });
      const normalized = normalizeLoadedArticle(saved);
      setArticle(normalized);
      showSuccess(resolvedArticleId ? "Articolo aggiornato." : "Articolo creato.");
      onSaved?.(normalized);
    } catch (error) {
      showError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    clearArticleEditorSession();
    setArticle(createEmptyArticle());
    setPreviewOpen(false);
    onReset?.();
  };

  const handleOpenList = async () => {
    setLoadingList(true);
    try {
      setSavedArticles(await listArticles());
      setOpenList(true);
    } catch (error) {
      showError(error.message);
    } finally {
      setLoadingList(false);
    }
  };

  const handlePickArticle = (id) => {
    setOpenList(false);
    setPreviewOpen(false);
    onOpenArticle?.(id);
  };

  const handleTogglePreview = () => {
    setPreviewOpen((current) => {
      const next = !current;
      if (next) {
        window.requestAnimationFrame(() => {
          previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
      return next;
    });
  };

  if (loading) {
    return (
      <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <>
      <AppBar position="static" color="default" elevation={0} sx={contentSectionMenubarSx}>
        <Toolbar sx={contentSectionMenubarToolbarSx}>
          <Button startIcon={<FolderOpenIcon />} onClick={handleOpenList} size="small" disabled={loadingList}>
            Apri
          </Button>
          <Button startIcon={<AddIcon />} onClick={handleReset} size="small">
            Nuovo
          </Button>
          <Button startIcon={<SaveIcon />} onClick={handleSave} size="small" disabled={saving}>
            {resolvedArticleId ? "Salva modifiche" : "Salva articolo"}
          </Button>
          <Button
            startIcon={<VisibilityOutlinedIcon />}
            onClick={handleTogglePreview}
            size="small"
            variant={previewOpen ? "contained" : "outlined"}
            sx={previewOpen ? { bgcolor: EDITOR_ACCENT, "&:hover": { bgcolor: "#092f6a" } } : undefined}
          >
            {previewOpen ? "CHIUDI ANTEPRIMA" : "VISUALIZZA ANTEPRIMA"}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Chip
            label={pageTitle}
            size="small"
            sx={{
              bgcolor: "#eef2ff",
              color: EDITOR_ACCENT,
              fontWeight: 600,
              maxWidth: 320,
              "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
            }}
          />
        </Toolbar>
      </AppBar>

      <Box sx={contentSectionBodySx}>
        {previewOpen ? (
          <ArticlePublicPreview ref={previewRef} article={article} onError={showError} />
        ) : null}

        <ArticleMetadataForm
          article={article}
          categories={categories}
          onChange={setArticle}
          onError={showError}
          onSuccess={showSuccess}
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 1.5,
            alignItems: "start",
          }}
        >
          <SuggestedProductsEditor
            products={article.suggestedProducts}
            onChange={(products) => setArticle((prev) => ({ ...prev, suggestedProducts: products }))}
          />
          <LinkedArticlesSelect
            selectedIds={article.linkedArticles}
            currentArticleId={resolvedArticleId}
            onChange={(linkedArticles) => setArticle((prev) => ({ ...prev, linkedArticles }))}
            onError={showError}
          />
        </Box>
        <ArticleContentEditor
          content={article.content}
          onChange={(content) => setArticle((prev) => ({ ...prev, content }))}
          onError={showError}
          onSuccess={showSuccess}
        />
      </Box>

      <Dialog open={openList} onClose={() => setOpenList(false)} fullWidth maxWidth="sm">
        <DialogTitle>Apri articolo</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {savedArticles.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              Nessun articolo salvato.
            </Typography>
          ) : (
            <List dense disablePadding>
              {savedArticles.map((item) => (
                <ListItemButton key={item.id} onClick={() => handlePickArticle(item.id)}>
                  <ListItemText
                    primary={item.title}
                    secondary={[item.category, item.isVisible ? "Visibile" : "Bozza"].filter(Boolean).join(" · ")}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenList(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ArticleEditorForm.propTypes = {
  articleId: PropTypes.string,
  onSaved: PropTypes.func,
  onReset: PropTypes.func,
  onOpenArticle: PropTypes.func,
};

export default ArticleEditorForm;
